import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Footer from "@/components/Footer";
import { API, api, WHATSAPP_NUMBER } from "@/lib/api";
import {
  HISTORIA,
  LUGAR,
  LLEGADA,
  RECOMENDACIONES,
  AQUAPARK,
  FAQ_PRIVATE,
  CONSEJOS,
  NATURALEZA,
  PRIVATE_CARDS,
  HERO_QUOTE,
  AMENIDADES,
  ESTANCIA,
  REGLAMENTO,
  ANTES_DE_SALIR,
  SECTION_HERO,
  PROVISIONES,
  PET_MANIFIESTO,
} from "@/lib/experienciaContent";

const HERO_IMG =
  "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85";

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between border-b border-stone-200 py-3">
      <span className="text-eyebrow text-stone-500 text-[10px]">{label}</span>
      <span className="font-serif-display text-lg text-stone-900">{value}</span>
    </div>
  );
}

/**
 * Tiny inline markdown renderer — turns `**bold**` fragments into <strong>.
 * Kept minimal on purpose (no full markdown). Safe: no HTML injected, only
 * text nodes wrapped in strong tags.
 */
function RichText({ text }) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return (
    <>
      {parts.map((chunk, i) =>
        chunk.startsWith("**") && chunk.endsWith("**") ? (
          <strong key={i} className="font-semibold text-stone-900">
            {chunk.slice(2, -2)}
          </strong>
        ) : (
          <span key={i}>{chunk}</span>
        )
      )}
    </>
  );
}

function Section({ id, sectionKey, eyebrow, title, children, dark = false }) {
  const key = sectionKey || id;
  const override = useCmsOverride(key);
  const finalEyebrow = override?.content?.eyebrow || eyebrow;
  const finalTitle = override?.content?.title || title;
  return (
    <section
      id={id}
      className={`py-20 md:py-28 border-t ${dark ? "bg-stone-900 text-stone-100 border-stone-800" : "border-stone-200 bg-[#FAFAF9]"}`}
    >
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        {finalEyebrow && (
          <p className={`text-eyebrow mb-4 ${dark ? "text-[#F2CC8F]" : "text-stone-500"}`}>
            {finalEyebrow}
          </p>
        )}
        {finalTitle && (
          <h2 className={`font-serif-display text-3xl md:text-5xl leading-tight mb-10 ${dark ? "text-white" : "text-stone-900"}`}>
            {finalTitle}
          </h2>
        )}
        {children}
      </div>
    </section>
  );
}

function SectionHero({ sectionKey, dark = false }) {
  const override = useCmsOverride(sectionKey);
  const heroDefault = SECTION_HERO[sectionKey];

  const overridePath = override?.hero?.storage_path;
  const src = overridePath
    ? `${API}/portal/assets/${overridePath}`
    : heroDefault?.src;
  const caption = override?.hero?.caption ?? heroDefault?.caption;
  const kind = overridePath ? (override?.hero?.kind || "image") : "image";

  if (!src) return null;
  return (
    <figure className="mb-12 md:mb-14" data-testid={`portal-hero-${sectionKey}`}>
      <div className="relative overflow-hidden">
        {kind === "video" ? (
          <video
            src={src}
            controls
            playsInline
            preload="metadata"
            className="w-full h-[280px] md:h-[420px] object-cover bg-black"
          />
        ) : (
          <img
            src={src}
            alt=""
            className="w-full h-[280px] md:h-[420px] object-cover"
            loading="lazy"
          />
        )}
      </div>
      {caption && (
        <figcaption className={`mt-4 text-[13px] italic font-serif-display ${dark ? "text-stone-400" : "text-stone-500"}`}>
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

/* Helper hook: returns CMS override for a given section key (or null) */
const CmsContext = createContext({});

function useCmsOverride(key) {
  const cms = useContext(CmsContext);
  return key ? cms[key] || null : null;
}

/* Small helper to override plain text fields (intro / criticalNote) inline */
function CmsText({ sectionKey, field, fallback, as: Tag = "span" }) {
  const override = useCmsOverride(sectionKey);
  const value = (override?.content?.[field] || "").trim() || fallback;
  if (!value) return null;
  return <Tag>{value}</Tag>;
}

export default function PortalHuesped() {
  const { token } = useParams();
  const [portal, setPortal] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cmsMap, setCmsMap] = useState({});
  const [acceptingPets, setAcceptingPets] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get(`/guest-portal/${encodeURIComponent(token)}`);
        if (mounted) setPortal(res.data);
      } catch (e) {
        if (mounted) {
          const msg = e?.response?.data?.detail || "Este enlace no es válido o expiró.";
          setError(typeof msg === "string" ? msg : "Este enlace no es válido o expiró.");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [token]);

  useEffect(() => {
    if (portal?.guest_name) {
      document.title = `${portal.guest_name} · Villas Pura Vida`;
    } else {
      document.title = "Portal del huésped · Villas Pura Vida";
    }
  }, [portal]);

  // Load CMS overrides (public endpoint — merges over static defaults)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/portal/content");
        if (!mounted) return;
        const map = {};
        for (const s of data?.sections || []) map[s.key] = s;
        setCmsMap(map);
      } catch (_e) {
        // Silent fallback to static defaults
      }
    })();
    return () => { mounted = false; };
  }, []);

  const openWA = (msg) => {
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const askHelp = () => {
    const g = portal?.guest_name ? ` (${portal.guest_name})` : "";
    openWA(`¡Hola! Necesito apoyo con mi estancia en Villas Pura Vida${g}.`);
  };

  const acceptPetRules = async () => {
    if (!portal?.has_pets || portal?.pets_accepted || acceptingPets) return;
    setAcceptingPets(true);
    try {
      const { data } = await api.post(`/guest-portal/${encodeURIComponent(token)}/accept-pet-rules`);
      setPortal((prev) => (prev ? { ...prev, pets_accepted: true, pets_accepted_at: data.pets_accepted_at } : prev));
    } catch (_e) {
      // Fallback: leave state as-is; user can retry
    } finally {
      setAcceptingPets(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
        <div className="text-stone-400 text-sm tracking-[0.28em] uppercase">Cargando…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAF9] px-6 text-center">
        <p className="text-eyebrow text-stone-500 mb-4">Portal del huésped</p>
        <h1 className="font-serif-display text-4xl md:text-5xl text-stone-900 mb-4">Enlace no válido</h1>
        <p className="text-stone-600 max-w-md mb-8" data-testid="portal-error-message">{error}</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 bg-[#E07A5F] hover:bg-[#D3674B] text-white px-8 py-3 text-sm uppercase tracking-[0.22em] font-semibold transition-all"
        >
          Volver al sitio
        </Link>
      </div>
    );
  }

  return (
    <CmsContext.Provider value={cmsMap}>
    <div data-testid="portal-page" className="min-h-screen bg-[#FAFAF9] text-stone-900">
      {/* Hero personalized */}
      <section className="relative min-h-[85vh] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-900/25 via-stone-900/15 to-stone-900/85" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-24 min-h-[85vh] flex flex-col justify-end">
          <p className="text-eyebrow text-stone-200 mb-6 fade-up">
            Bienvenido a Villas Pura Vida
          </p>
          <h1
            data-testid="portal-hero-title"
            className="font-serif-display text-white text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight mb-6 fade-up fade-up-delay-1"
          >
            Hola,{" "}
            <span className="italic font-light text-[#F2CC8F]">
              {portal?.guest_name || "huésped"}.
            </span>
          </h1>
          <p className="text-stone-100 text-lg md:text-xl max-w-2xl mb-8 leading-relaxed fade-up fade-up-delay-2">
            {HERO_QUOTE}
          </p>
          <p
            data-testid="portal-brand-slogan"
            className="font-serif-display italic text-[#F2CC8F]/90 text-xl md:text-2xl max-w-2xl mb-6 leading-snug fade-up fade-up-delay-2"
          >
            Los lugares se heredan. Los recuerdos también.
          </p>
          {portal?.property_label && (
            <p className="text-eyebrow text-[#F2CC8F] fade-up fade-up-delay-3" data-testid="portal-property-label">
              Estancia en {portal.property_label}
            </p>
          )}
        </div>
      </section>

      {/* Info card row */}
      {(portal?.check_in || portal?.check_out || portal?.guests) && (
        <section className="max-w-4xl mx-auto px-6 md:px-10 -mt-16 relative z-20 pb-16">
          <div className="bg-white border border-stone-200 shadow-lg p-8 md:p-10">
            <p className="text-eyebrow text-stone-500 mb-4">Tu reserva</p>
            <InfoRow label="Alojamiento" value={portal.property_label} />
            <InfoRow label="Check-in" value={portal.check_in ? `${portal.check_in} · 3:00 pm` : null} />
            <InfoRow label="Check-out" value={portal.check_out ? `${portal.check_out} · 12:00 pm` : null} />
            {portal.guests ? <InfoRow label="Huéspedes" value={portal.guests} /> : null}
          </div>
        </section>
      )}

      {/* Síguenos — presencia desde la reserva (versión superior compacta) */}
      <section
        id="siguenos"
        className="max-w-4xl mx-auto px-6 md:px-10 pb-16"
        data-testid="portal-siguenos-top"
      >
        <div className="border border-stone-200 bg-[#FAFAF9] p-6 md:p-10 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 md:gap-10 items-center">
          {/* QR compacto */}
          <div className="w-[180px] mx-auto md:mx-0">
            <img
              src="/brand/ig-qr.png"
              alt="Código QR de Instagram — @villaspuravida.tuxpan"
              className="w-full h-auto border border-stone-200 bg-white p-2"
              data-testid="portal-siguenos-top-qr"
            />
            <p className="text-eyebrow text-stone-500 mt-2 text-[10px] text-center md:text-left">
              Escanea para seguirnos
            </p>
          </div>

          {/* Copy + redes en fila */}
          <div>
            <p className="text-eyebrow text-stone-500 mb-2">Estamos contigo desde ahora</p>
            <h3 className="font-serif-display text-2xl md:text-3xl leading-snug text-stone-900 mb-3">
              Síguenos y prepárate <span className="italic text-stone-500">como local.</span>
            </h3>
            <p className="text-stone-700 leading-relaxed text-[15px] mb-5">
              Desde el momento en que reservas, compartimos por redes: <strong>promociones exclusivas</strong>, consejos para viajar con niños, tips del clima, recomendaciones locales de la zona y detalles del lugar que hacen la estancia más cómoda.
            </p>
            <div className="flex flex-wrap gap-4 items-center">
              <a
                href="https://www.instagram.com/villaspuravida.tuxpan/"
                target="_blank"
                rel="noreferrer"
                data-testid="portal-siguenos-top-instagram"
                className="inline-flex items-center gap-2 text-stone-900 hover:text-[#E07A5F] transition-colors"
                aria-label="Instagram"
              >
                <i className="fa-brands fa-instagram text-2xl" />
                <span className="font-serif-display italic text-[15px]">@villaspuravida.tuxpan</span>
              </a>
              <a
                href="https://www.facebook.com/share/1BkkG4kNE2/?mibextid=wwXIfr"
                target="_blank"
                rel="noreferrer"
                data-testid="portal-siguenos-top-facebook"
                className="inline-flex items-center gap-2 text-stone-900 hover:text-[#E07A5F] transition-colors"
                aria-label="Facebook"
              >
                <i className="fa-brands fa-facebook text-2xl" />
                <span className="font-serif-display italic text-[15px]">Villas Pura Vida</span>
              </a>
              <a
                href="https://www.tiktok.com/@villas.pura.vida"
                target="_blank"
                rel="noreferrer"
                data-testid="portal-siguenos-top-tiktok"
                className="inline-flex items-center gap-2 text-stone-900 hover:text-[#E07A5F] transition-colors"
                aria-label="TikTok"
              >
                <i className="fa-brands fa-tiktok text-2xl" />
                <span className="font-serif-display italic text-[15px]">@villas.pura.vida</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Card grid — fondo pastel por card */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <p className="text-eyebrow text-stone-500 mb-4">Tu experiencia</p>
        <h2 className="font-serif-display text-4xl md:text-5xl leading-tight text-stone-900 mb-12 max-w-3xl">
          Todo lo que necesitas,
          <br />
          <span className="italic text-stone-500">en un solo lugar.</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(portal?.has_pets ? [
            {
              key: "mascotas",
              icon: "paw",
              label: "Mascotas — Manifiesto",
              body: portal?.pets_accepted
                ? "Reglas aceptadas. Gracias por cuidar el lugar."
                : "Léelo y acéptalo antes del check-in.",
              bg: portal?.pets_accepted ? "#E4EDE7" : "#FBEEEA",
              accent: portal?.pets_accepted ? "#5F8B76" : "#B84A2C",
              critical: !portal?.pets_accepted,
            },
            ...PRIVATE_CARDS,
          ] : PRIVATE_CARDS).map((card) => (
            <a
              key={card.key}
              href={`#${card.key}`}
              data-testid={`portal-card-${card.key}`}
              className={`group flex flex-col h-full p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-lg ${
                card.critical ? "ring-1 ring-[#B84A2C]" : ""
              }`}
              style={{ backgroundColor: card.bg }}
            >
              <div className="flex items-start justify-between mb-6">
                <i className={`fa-solid fa-${card.icon} text-[22px]`} style={{ color: card.accent }} />
                <span
                  className="text-[10px] tracking-[0.28em] uppercase"
                  style={{ color: card.critical ? "#B84A2C" : "#78716c" }}
                >
                  {card.critical ? "Obligatorio" : "—"}
                </span>
              </div>
              <h3 className="font-serif-display text-2xl leading-tight text-stone-900 mb-2">{card.label}</h3>
              <p className="text-stone-700 text-[15px] leading-relaxed flex-1">{card.body}</p>
              <div className="mt-6 flex items-center text-[11px] tracking-[0.32em] uppercase text-stone-800 group-hover:text-stone-900 transition-colors duration-500">
                <span>Ver</span>
                <i className="fa-solid fa-arrow-right ml-3 text-[10px] group-hover:translate-x-1 transition-transform duration-500" />
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Manifiesto de Mascotas — solo si has_pets */}
      {portal?.has_pets && (
        <section
          id="mascotas"
          data-testid="portal-pet-manifiesto"
          className={`py-20 md:py-28 border-t ${
            portal?.pets_accepted
              ? "border-[#D9EAD3] bg-[#F1F7EA]"
              : "border-[#F0CFC0] bg-[#FBEEEA]"
          }`}
        >
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <p
              className="text-eyebrow mb-4"
              style={{ color: portal?.pets_accepted ? "#5F8B76" : "#B84A2C" }}
            >
              {PET_MANIFIESTO.eyebrow}
            </p>
            <h2 className="font-serif-display text-3xl md:text-5xl leading-tight text-stone-900 mb-6">
              {PET_MANIFIESTO.title}
            </h2>
            <p className="text-stone-700 leading-relaxed text-[15px] md:text-base max-w-2xl mb-8">
              {PET_MANIFIESTO.intro}
            </p>

            {!portal?.pets_accepted && (
              <div
                className="border-l-4 pl-5 py-4 pr-6 mb-10 flex gap-3 items-start"
                style={{ borderColor: "#B84A2C", backgroundColor: "#F7DBD1" }}
                data-testid="portal-pet-critical-note"
              >
                <i className="fa-solid fa-triangle-exclamation text-[22px]" style={{ color: "#B84A2C" }} />
                <div>
                  <p className="text-eyebrow mb-1" style={{ color: "#7A2E17" }}>MUY IMPORTANTE</p>
                  <p className="text-stone-800 text-[15px] leading-relaxed">
                    {PET_MANIFIESTO.criticalNote}
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
              {PET_MANIFIESTO.rules.map((r, i) => (
                <div
                  key={r.title}
                  data-testid={`portal-pet-rule-${i + 1}`}
                  className="bg-white border border-stone-200 p-6 flex gap-4"
                >
                  <div
                    className="w-10 h-10 flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: portal?.pets_accepted ? "#E4EDE7" : "#FBEEEA" }}
                  >
                    <i
                      className={`fa-solid fa-${r.icon} text-[18px]`}
                      style={{ color: portal?.pets_accepted ? "#5F8B76" : "#B84A2C" }}
                    />
                  </div>
                  <div className="flex-1">
                    <p className="font-serif-display text-lg text-stone-900 leading-tight mb-2">
                      {r.title}
                    </p>
                    <p className="text-stone-700 text-[14px] leading-relaxed">{r.body}</p>
                  </div>
                </div>
              ))}
            </div>

            {portal?.pets_accepted ? (
              <div
                className="border p-6 flex items-center gap-4"
                style={{ borderColor: "#B0C7B6", backgroundColor: "#E4EDE7" }}
                data-testid="portal-pet-accepted-box"
              >
                <div
                  className="w-12 h-12 flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "#5F8B76" }}
                >
                  <i className="fa-solid fa-check text-white text-[20px]" />
                </div>
                <div>
                  <p className="font-serif-display text-xl text-stone-900 leading-tight mb-1">
                    {PET_MANIFIESTO.acceptedLabel}
                  </p>
                  <p className="text-stone-700 text-[14px] leading-relaxed">
                    {PET_MANIFIESTO.acceptSuccessMessage}
                    {portal?.pets_accepted_at && (
                      <span className="italic text-stone-500 ml-1">
                        · {new Date(portal.pets_accepted_at).toLocaleString("es-MX", {
                          day: "numeric", month: "short", year: "numeric",
                          hour: "2-digit", minute: "2-digit",
                        })}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  onClick={acceptPetRules}
                  disabled={acceptingPets}
                  data-testid="portal-pet-accept-btn"
                  className="inline-flex items-center gap-2 bg-[#B84A2C] hover:bg-[#9A3E23] disabled:opacity-70 text-white px-8 py-4 text-sm uppercase tracking-[0.22em] font-semibold transition-all"
                >
                  {acceptingPets ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin" />
                      Registrando…
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-check" />
                      {PET_MANIFIESTO.acceptButtonLabel}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={askHelp}
                  className="inline-flex items-center gap-2 text-stone-700 hover:text-[#25D366] text-sm uppercase tracking-[0.22em] font-semibold transition-all"
                >
                  <i className="fa-brands fa-whatsapp" />
                  Tengo dudas
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Estancia */}
      <Section id="estancia" eyebrow={ESTANCIA.eyebrow} title={ESTANCIA.title}>
        <SectionHero sectionKey="estancia" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
          {ESTANCIA.items.map((it) => (
            <div key={it.label} className="border-t border-stone-300 pt-6" data-testid={`portal-estancia-${it.label.toLowerCase().replace(/\s/g, "-")}`}>
              <p className="text-eyebrow text-stone-500 mb-2">{it.label}</p>
              <p className="font-serif-display text-3xl md:text-4xl text-stone-900 leading-none mb-3">{it.value}</p>
              <p className="text-stone-600 leading-relaxed text-[15px]">{it.note}</p>
            </div>
          ))}
        </div>
        <div className="mt-14 flex flex-wrap gap-4">
          <button
            type="button"
            onClick={askHelp}
            data-testid="portal-btn-ayuda"
            className="inline-flex items-center gap-2 bg-[#E07A5F] hover:bg-[#D3674B] text-white px-6 py-3 text-sm uppercase tracking-[0.22em] font-semibold transition-all"
          >
            <i className="fa-brands fa-whatsapp" />
            Escribir por WhatsApp
          </button>
        </div>
      </Section>

      {/* Amenidades */}
      <Section id="amenidades" eyebrow={AMENIDADES.eyebrow} title={AMENIDADES.title}>
        <p className="text-stone-700 text-lg leading-relaxed max-w-3xl mb-10">{AMENIDADES.intro}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {AMENIDADES.groups.map((g) => (
            <div
              key={g.label}
              className="bg-white border border-stone-200 p-8"
              data-testid={`portal-amenidades-${g.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div className="flex items-center gap-4 mb-6">
                <i className={`fa-solid fa-${g.icon} text-2xl text-[#E07A5F]`} />
                <h3 className="font-serif-display text-2xl text-stone-900">{g.label}</h3>
              </div>
              <ul className="space-y-3">
                {g.items.map((item) => (
                  <li key={item} className="flex gap-3 items-start text-stone-700 leading-relaxed">
                    <span className="text-[#F2CC8F] mt-2 text-[8px]">●</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      {/* Antes de venir · Provisiones — sección crítica */}
      <Section id="provisiones" eyebrow={PROVISIONES.eyebrow} title={PROVISIONES.title}>
        <div
          data-testid="provisiones-critical-note"
          className="flex gap-4 items-start p-5 mb-12 border-l-4 border-[#B84A2C] bg-[#FBEEEA] max-w-4xl"
        >
          <i className="fa-solid fa-triangle-exclamation text-[#B84A2C] text-xl mt-0.5" />
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase font-semibold text-[#B84A2C] mb-1">
              Muy importante
            </p>
            <p className="text-stone-800 leading-relaxed text-[15px]">{PROVISIONES.criticalNote}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <div data-testid="provisiones-bring" className="p-8" style={{ backgroundColor: "#F3E4DB" }}>
            <div className="flex items-center gap-4 mb-6">
              <i className={`fa-solid fa-${PROVISIONES.bring.icon} text-2xl text-[#E07A5F]`} />
              <h3 className="font-serif-display text-2xl text-stone-900">{PROVISIONES.bring.label}</h3>
            </div>
            <ul className="space-y-3">
              {PROVISIONES.bring.items.map((it) => (
                <li key={it} className="flex gap-3 items-start text-stone-800 leading-relaxed">
                  <span className="text-[#E07A5F] mt-2 text-[8px]">●</span>
                  <span><RichText text={it} /></span>
                </li>
              ))}
            </ul>
          </div>

          <div data-testid="provisiones-distances" className="p-8" style={{ backgroundColor: "#F7EED8" }}>
            <div className="flex items-center gap-4 mb-6">
              <i className={`fa-solid fa-${PROVISIONES.distances.icon} text-2xl text-[#B8873B]`} />
              <h3 className="font-serif-display text-2xl text-stone-900">{PROVISIONES.distances.label}</h3>
            </div>
            <ul className="space-y-3">
              {PROVISIONES.distances.items.map((it) => (
                <li key={it} className="flex gap-3 items-start text-stone-800 leading-relaxed">
                  <span className="text-[#B8873B] mt-2 text-[8px]">●</span>
                  <span><RichText text={it} /></span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div
          data-testid="provisiones-mandados"
          className="relative p-8 md:p-10 border border-[#5F8B76]/40"
          style={{ backgroundColor: "#E4EDE7" }}
        >
          <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-8 items-center">
            <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center bg-[#5F8B76]">
              <i className={`fa-solid fa-${PROVISIONES.mandados.icon} text-3xl text-white`} />
            </div>
            <div>
              <p className="text-[10px] tracking-[0.32em] uppercase font-semibold text-[#5F8B76] mb-2">
                Servicio útil
              </p>
              <h3 className="font-serif-display text-2xl md:text-3xl text-stone-900 mb-3">
                {PROVISIONES.mandados.label}
              </h3>
              <p className="text-stone-800 leading-relaxed text-[15px] max-w-2xl">
                {PROVISIONES.mandados.body}
              </p>
            </div>
            <div className="text-center md:text-right md:pl-6 md:border-l md:border-[#5F8B76]/30">
              <p className="text-[10px] tracking-[0.32em] uppercase text-stone-600 mb-1">Costo</p>
              <p className="font-serif-display text-4xl md:text-5xl text-[#5F8B76] leading-none">
                {PROVISIONES.mandados.price}
              </p>
              <p className="text-[11px] tracking-[0.2em] uppercase text-stone-600 mt-2">
                {PROVISIONES.mandados.unit}
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Llegar */}
      <Section id="llegar" eyebrow={LLEGADA.eyebrow} title={LLEGADA.title}>
        <SectionHero sectionKey="llegar" />
        <p className="text-stone-700 text-lg leading-relaxed mb-10 max-w-3xl">{LLEGADA.intro}</p>
        <ol className="space-y-8">
          {LLEGADA.references.map((r) => (
            <li key={r.step} className="grid grid-cols-[auto_1fr] gap-6 md:gap-10 items-start pb-8 border-b border-stone-300">
              <span className="font-serif-display text-4xl md:text-5xl text-stone-400 italic">{r.step}</span>
              <div>
                <h3 className="font-serif-display text-2xl text-stone-900 mb-1">{r.label}</h3>
                <p className="text-stone-600">{r.body}</p>
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-10 p-6 bg-[#F2CC8F]/20 border-l-2 border-[#E07A5F]">
          <p className="text-eyebrow text-stone-700 mb-2">Si llovió</p>
          <p className="text-stone-800 leading-relaxed">{LLEGADA.weather}</p>
        </div>
      </Section>

      {/* Lugar */}
      <Section id="lugar" eyebrow={LUGAR.eyebrow} title={LUGAR.title}>
        <SectionHero sectionKey="lugar" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
          {LUGAR.items.map((it) => (
            <div key={it.label} className="border-t border-stone-300 pt-6">
              <i className={`fa-solid fa-${it.icon} text-xl text-[#E07A5F] mb-3 block`} />
              <h3 className="font-serif-display text-xl text-stone-900 mb-2">{it.label}</h3>
              <p className="text-stone-600 leading-relaxed text-[15px]">{it.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Historia */}
      <Section id="historia" eyebrow={HISTORIA.eyebrow} title={HISTORIA.title} dark>
        <SectionHero sectionKey="historia" dark />
        <div className="space-y-6 text-lg leading-relaxed text-stone-200 max-w-3xl">
          {HISTORIA.body.map((p, i) => (
            <p key={i} className={i === HISTORIA.body.length - 1 ? "font-serif-display italic text-2xl text-[#F2CC8F]" : ""}>
              {p}
            </p>
          ))}
        </div>
        <div className="mt-14 pt-10 border-t border-stone-700 max-w-2xl">
          <p className="font-serif-display italic text-2xl md:text-3xl text-white leading-tight">
            &ldquo;{HISTORIA.quote}&rdquo;
          </p>
        </div>
      </Section>

      {/* Consejos */}
      <Section id="consejos" eyebrow={CONSEJOS.eyebrow} title={CONSEJOS.title}>
        <ul className="space-y-6 max-w-3xl">
          {CONSEJOS.tips.map((tip, i) => (
            <li key={i} className="grid grid-cols-[auto_1fr] gap-5 items-start" data-testid={`portal-consejo-${i + 1}`}>
              <span className="font-serif-display text-2xl text-stone-400 italic">{String(i + 1).padStart(2, "0")}</span>
              <p className="text-stone-700 leading-relaxed text-lg">{tip}</p>
            </li>
          ))}
        </ul>
      </Section>

      {/* Naturaleza */}
      <Section id="naturaleza" eyebrow={NATURALEZA.eyebrow} title={NATURALEZA.title}>
        <SectionHero sectionKey="naturaleza" />
        <p className="text-stone-700 leading-relaxed text-lg max-w-3xl mb-8">{NATURALEZA.intro}</p>

        {NATURALEZA.criticalNote && (
          <div
            data-testid="naturaleza-critical-note"
            className="flex gap-4 items-start p-5 mb-10 border-l-4 border-[#B84A2C] bg-[#FBEEEA] max-w-3xl"
          >
            <i className="fa-solid fa-triangle-exclamation text-[#B84A2C] text-xl mt-0.5" />
            <div>
              <p className="text-[10px] tracking-[0.32em] uppercase font-semibold text-[#B84A2C] mb-1">
                Muy importante
              </p>
              <p className="text-stone-800 leading-relaxed text-[15px]">
                {NATURALEZA.criticalNote}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {NATURALEZA.items.map((it) => {
            if (it.critical) {
              return (
                <div
                  key={it.label}
                  data-testid={`naturaleza-item-critical-${it.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="relative flex gap-5 items-start p-6 bg-[#FBEEEA] border-l-4 border-[#B84A2C]"
                >
                  <i className={`fa-solid fa-${it.icon} text-xl text-[#B84A2C] mt-1`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-serif-display text-xl text-[#7A2E17]">{it.label}</h3>
                      <span className="inline-flex items-center gap-1 text-[9px] tracking-[0.28em] uppercase font-semibold text-white bg-[#B84A2C] px-2 py-0.5">
                        Esencial
                      </span>
                    </div>
                    <p className="text-stone-800 leading-relaxed text-[15px]">{it.body}</p>
                  </div>
                </div>
              );
            }
            return (
              <div key={it.label} className="flex gap-5 items-start p-6 bg-white border border-stone-200">
                <i className={`fa-solid fa-${it.icon} text-xl text-[#E07A5F] mt-1`} />
                <div>
                  <h3 className="font-serif-display text-xl text-stone-900 mb-1">{it.label}</h3>
                  <p className="text-stone-600 leading-relaxed text-[15px]">{it.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Asador */}
      <Section id="asador" eyebrow="RESERVA DEL ASADOR" title="Coordina tu horario.">
        <div className="max-w-2xl">
          <p className="text-stone-700 leading-relaxed">
            La palapa del asador (~10 × 5 m) funciona con coordinación cuando hay otros huéspedes en el conjunto. Para su uso, se debe preguntar con las personas encargadas del lugar la disponibilidad de días y horarios, para poder reservarlo y hacer uso de este servicio.
          </p>
        </div>
      </Section>

      {/* Reglamento */}
      <Section id="reglamento" eyebrow={REGLAMENTO.eyebrow} title={REGLAMENTO.title} dark>
        <p className="text-stone-200 text-lg leading-relaxed max-w-3xl mb-12">{REGLAMENTO.intro}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {REGLAMENTO.groups.map((g) => (
            <div
              key={g.label}
              className="bg-stone-800/60 border border-stone-700 p-8"
              data-testid={`portal-reglamento-${g.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div className="flex items-center gap-4 mb-6">
                <i className={`fa-solid fa-${g.icon} text-2xl text-[#F2CC8F]`} />
                <h3 className="font-serif-display text-2xl text-white">{g.label}</h3>
              </div>
              <ul className="space-y-3">
                {g.rules.map((rule) => (
                  <li key={rule} className="flex gap-3 items-start text-stone-200 leading-relaxed">
                    <span className="text-[#E07A5F] mt-2 text-[8px]">●</span>
                    <span>{rule}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-14 pt-10 border-t border-stone-700 max-w-3xl">
          <p className="font-serif-display italic text-2xl md:text-3xl text-[#F2CC8F] leading-tight">
            &ldquo;{REGLAMENTO.golden}&rdquo;
          </p>
        </div>
      </Section>

      {/* FAQ */}
      <Section id="faq" eyebrow="PREGUNTAS FRECUENTES" title="Todo lo que quieres saber.">
        <div className="space-y-8 max-w-3xl">
          {FAQ_PRIVATE.map((item) => (
            <div key={item.q} className="border-t border-stone-300 pt-6">
              <h3 className="font-serif-display italic text-2xl text-stone-900 mb-3">{item.q}</h3>
              <p className="text-stone-600 leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Recomendaciones */}
      <Section id="recomendaciones" eyebrow={RECOMENDACIONES.eyebrow} title={RECOMENDACIONES.title}>
        {/* --- Comida preparada por los encargados -------------------- */}
        {RECOMENDACIONES.comidaPreparada && (
          <div
            className="flex gap-5 items-start p-6 md:p-8 bg-[#F3E4DB] border-l-4 border-[#E07A5F] mb-6"
            data-testid="portal-comida-preparada"
          >
            <i className={`fa-solid fa-${RECOMENDACIONES.comidaPreparada.icon} text-2xl text-[#E07A5F] mt-1`} />
            <div>
              <h3 className="font-serif-display text-xl md:text-2xl text-stone-900 mb-2">
                {RECOMENDACIONES.comidaPreparada.label}
              </h3>
              <p className="text-stone-700 leading-relaxed text-[15px]">
                <RichText text={RECOMENDACIONES.comidaPreparada.body} />
              </p>
            </div>
          </div>
        )}

        {/* --- Salida a Tuxpan · 5 lugares recomendados -------------- */}
        {RECOMENDACIONES.salidaTuxpan && (
          <div className="bg-white border border-stone-200 mb-10" data-testid="portal-salida-tuxpan">
            <div className="p-6 md:p-8 flex gap-5 items-start border-b border-stone-200">
              <i className={`fa-solid fa-${RECOMENDACIONES.salidaTuxpan.icon} text-2xl text-[#5F8B76] mt-1`} />
              <div>
                <h3 className="font-serif-display text-xl md:text-2xl text-stone-900 mb-2">
                  {RECOMENDACIONES.salidaTuxpan.label}
                </h3>
                <p className="text-stone-700 leading-relaxed text-[15px]">
                  <RichText text={RECOMENDACIONES.salidaTuxpan.body} />
                </p>
              </div>
            </div>

            {/* Infografía */}
            {RECOMENDACIONES.salidaTuxpan.infographic && (
              <figure className="p-6 md:p-8 bg-[#FAFAF9] border-b border-stone-200">
                <img
                  src={RECOMENDACIONES.salidaTuxpan.infographic}
                  alt={RECOMENDACIONES.salidaTuxpan.infographicCaption || "5 lugares recomendados en Tuxpan"}
                  className="w-full max-w-3xl mx-auto h-auto border border-stone-200"
                  data-testid="portal-5-lugares-infografia"
                />
                {RECOMENDACIONES.salidaTuxpan.infographicCaption && (
                  <figcaption className="text-eyebrow text-stone-500 mt-4 text-center">
                    {RECOMENDACIONES.salidaTuxpan.infographicCaption}
                  </figcaption>
                )}
              </figure>
            )}

            {/* Lista con detalles */}
            <ol className="divide-y divide-stone-200" data-testid="portal-5-lugares-lista">
              {RECOMENDACIONES.salidaTuxpan.lugares.map((l) => (
                <li key={l.n} className="p-6 md:p-8 flex gap-5 md:gap-6 items-start">
                  <span className="shrink-0 w-11 h-11 rounded-full bg-[#5F8B76] text-white font-serif-display text-lg flex items-center justify-center">
                    {l.n}
                  </span>
                  <div className="min-w-0">
                    <h4 className="font-serif-display text-lg text-stone-900">{l.name}</h4>
                    {l.subtitle && <p className="text-sm text-[#5F8B76] italic mb-2">{l.subtitle}</p>}
                    <p className="text-stone-700 leading-relaxed text-[15px] mb-2">{l.desc}</p>
                    <p className="text-eyebrow text-stone-500 mb-1">
                      <i className="fa-solid fa-star mr-1 text-[#B8873B]" /> {l.rating}
                    </p>
                    <p className="text-stone-500 text-[13px] flex items-start gap-2">
                      <i className="fa-solid fa-location-dot mt-1 text-[#5F8B76]" />
                      <span>{l.address}</span>
                    </p>
                  </div>
                </li>
              ))}
            </ol>

            {RECOMENDACIONES.salidaTuxpan.tip && (
              <p className="p-6 md:p-8 bg-[#E4EDE7] text-stone-700 leading-relaxed text-[15px] border-t border-stone-200">
                <RichText text={RECOMENDACIONES.salidaTuxpan.tip} />
              </p>
            )}
          </div>
        )}

        {/* --- Otras categorías cortas ------------------------------- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {RECOMENDACIONES.categories.map((cat) => (
            <div key={cat.label} className="flex gap-5 items-start p-6 bg-white border border-stone-200">
              <i className={`fa-solid fa-${cat.icon} text-xl text-[#E07A5F] mt-1`} />
              <div>
                <h3 className="font-serif-display text-xl text-stone-900 mb-1">{cat.label}</h3>
                <p className="text-stone-600 leading-relaxed text-[15px]">{cat.body}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Parque acuático — AquaPark Tuxpan */}
      <Section id="aquapark" eyebrow={AQUAPARK.eyebrow} title={AQUAPARK.title}>
        <p className="text-stone-700 leading-relaxed text-[16px] md:text-[17px] mb-10 max-w-3xl">
          {AQUAPARK.intro}
        </p>

        {/* Datos rápidos */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-10">
          {AQUAPARK.quickFacts.map((f) => (
            <div key={f.label} className="p-5 md:p-6 bg-white border border-stone-200">
              <i className={`fa-solid fa-${f.icon} text-xl text-[#3D5A80] mb-3 block`} />
              <p className="text-eyebrow text-stone-500 mb-2">{f.label}</p>
              <p className="text-stone-800 leading-relaxed text-[15px]">{f.value}</p>
            </div>
          ))}
        </div>

        {/* Precios */}
        <div className="bg-[#E5EAF0] border-l-4 border-[#3D5A80] p-6 md:p-8 mb-10" data-testid="portal-aquapark-precios">
          <div className="flex gap-5 items-start mb-5">
            <i className={`fa-solid fa-${AQUAPARK.prices.icon} text-2xl text-[#3D5A80] mt-1`} />
            <h3 className="font-serif-display text-xl md:text-2xl text-stone-900">
              {AQUAPARK.prices.label}
            </h3>
          </div>
          <ul className="divide-y divide-[#B4C0D0]">
            {AQUAPARK.prices.items.map((it) => (
              <li key={it.label} className="flex items-baseline justify-between py-3">
                <span className="text-stone-700 text-[15px]">{it.label}</span>
                <span className="font-serif-display text-xl text-stone-900">{it.value}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Highlights */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {AQUAPARK.features.map((f) => (
            <div key={f.label} className="p-5 bg-white border border-stone-200 text-center">
              <i className={`fa-solid fa-${f.icon} text-2xl text-[#3D5A80] mb-3 block`} />
              <p className="text-stone-700 text-[14px] leading-snug">{f.label}</p>
            </div>
          ))}
        </div>

        {/* Disclaimer */}
        <p className="text-[13px] text-stone-500 italic border-t border-stone-200 pt-6 max-w-3xl">
          <i className="fa-solid fa-circle-info mr-2 text-stone-400" />
          {AQUAPARK.disclaimer}
        </p>
      </Section>

      {/* Recuerdos */}
      <Section id="recuerdos" eyebrow="COMPARTE TUS RECUERDOS" title="Etiquétanos.">
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-10 md:gap-14 items-start max-w-4xl">
          {/* QR de Instagram */}
          <div className="w-full max-w-[280px] mx-auto md:mx-0">
            <img
              src="/brand/ig-qr.png"
              alt="Código QR de Instagram — @villaspuravida.tuxpan"
              className="w-full h-auto border border-stone-200 bg-[#FAFAF9] p-3"
              data-testid="portal-instagram-qr"
            />
            <p className="text-eyebrow text-stone-500 mt-3 text-center md:text-left">
              Escanea para seguirnos
            </p>
          </div>

          {/* Copy + redes */}
          <div>
            <p className="text-stone-700 leading-relaxed mb-4">
              Nos encantaría ver tu viaje. Si tú y tus acompañantes comparten fotos o historias de la estancia,
              etiquétanos — así podemos revivir el lugar a través de sus ojos y compartir sus recuerdos con la comunidad.
            </p>
            <p className="text-stone-500 text-[15px] italic mb-8">
              Hashtag oficial: <span className="not-italic font-serif-display text-stone-900">#VillasPuraVida</span>
            </p>

            <ul className="space-y-4">
              <li>
                <a
                  href="https://www.instagram.com/villaspuravida.tuxpan/"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="portal-instagram-link"
                  className="inline-flex items-center gap-4 text-stone-900 hover:text-[#E07A5F] transition-colors group"
                >
                  <i className="fa-brands fa-instagram text-2xl w-7" />
                  <span className="font-serif-display italic text-xl">@villaspuravida.tuxpan</span>
                  <span className="text-xs uppercase tracking-[0.22em] text-stone-400 group-hover:text-[#E07A5F]">
                    Instagram
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.facebook.com/share/1BkkG4kNE2/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="portal-facebook-link"
                  className="inline-flex items-center gap-4 text-stone-900 hover:text-[#E07A5F] transition-colors group"
                >
                  <i className="fa-brands fa-facebook text-2xl w-7" />
                  <span className="font-serif-display italic text-xl">Villas Pura Vida</span>
                  <span className="text-xs uppercase tracking-[0.22em] text-stone-400 group-hover:text-[#E07A5F]">
                    Facebook
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.tiktok.com/@villas.pura.vida"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="portal-tiktok-link"
                  className="inline-flex items-center gap-4 text-stone-900 hover:text-[#E07A5F] transition-colors group"
                >
                  <i className="fa-brands fa-tiktok text-2xl w-7" />
                  <span className="font-serif-display italic text-xl">@villas.pura.vida</span>
                  <span className="text-xs uppercase tracking-[0.22em] text-stone-400 group-hover:text-[#E07A5F]">
                    TikTok
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </Section>

      {/* Antes de irte */}
      <Section id="antes" eyebrow="ANTES DE IRTE" title="Cómo cerrar tu estancia." dark>
        <SectionHero sectionKey="antes" dark />
        <ul className="space-y-6 max-w-3xl text-stone-200 leading-relaxed">
          {ANTES_DE_SALIR.map((it, i) => (
            <li
              key={it.label}
              className="grid grid-cols-[auto_1fr] gap-5 items-start"
              data-testid={`portal-antes-${i + 1}`}
            >
              <i className={`fa-${it.icon === "whatsapp" ? "brands" : "solid"} fa-${it.icon} text-[#F2CC8F] mt-1`} />
              <span>{it.label}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Footer />
    </div>
    </CmsContext.Provider>
  );
}
